// utils/sessionStore.js
//
// The ONE place the sign-in tokens are kept: the 1-hour access pass, the renewable token and the «تذكرني»
// choice. Everything that reads or writes them in the core sign-in code (tokenValidator, sessionRenewal,
// AuthContext, secureApi) goes through here.
//
// Why a seam: the web keeps them in browser storage (shared by tabs — the behaviour below is exactly what
// the code did before this file existed), while the owner store app must keep the pass in memory and the
// renewable token in the phone's Keychain/Keystore, never in browser storage
// (tasks/owner-mobile-app-build.md §2 decision 6, §10 step 8). The app installs its own store with
// installSessionStore() as the very first thing its entry point does, before any of the modules above load.
//
// The interface is synchronous on purpose: the existing callers (logout, storeTokenPair, the axios
// interceptors) are synchronous and not awaited. A store backed by asynchronous storage keeps a copy in
// memory and persists behind it (see owner/app/session/appSessionStore.js).

const TOKEN = 'token';
const REFRESH_TOKEN = 'refreshToken';
const REMEMBERED = 'sessionRemembered';

/** Browser storage, as the web has always used it. */
export const webSessionStore = {
  getAccessToken: () => localStorage.getItem(TOKEN) || sessionStorage.getItem(TOKEN),
  getRefreshToken: () => localStorage.getItem(REFRESH_TOKEN),
  setTokens: ({ accessToken, refreshToken }) => {
    localStorage.setItem(TOKEN, accessToken);
    sessionStorage.setItem(TOKEN, accessToken);
    if (refreshToken) {
      localStorage.setItem(REFRESH_TOKEN, refreshToken);
    }
  },
  clear: () => {
    [TOKEN, REFRESH_TOKEN, REMEMBERED].forEach((key) => {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    });
  },
  setRemembered: (remember) => {
    localStorage.setItem(REMEMBERED, remember ? '1' : '0');
  },
  // A sign-in from before the «تذكرني» key existed has no value and is treated as remembered.
  isRemembered: () => localStorage.getItem(REMEMBERED) !== '0'
};

let activeStore = webSessionStore;

/**
 * Replaces the store. Called once, by the owner app's entry point, before anything reads a token.
 * The web never calls it.
 */
export const installSessionStore = (store) => {
  activeStore = store;
};

// Callers go through these, never through activeStore directly, so an installed store is always honoured.
const sessionStore = {
  getAccessToken: () => activeStore.getAccessToken(),
  getRefreshToken: () => activeStore.getRefreshToken(),
  setTokens: (tokens) => activeStore.setTokens(tokens),
  clear: () => activeStore.clear(),
  setRemembered: (remember) => activeStore.setRemembered(remember),
  isRemembered: () => activeStore.isRemembered()
};

export default sessionStore;
